import React, { useState, useEffect, useCallback } from 'react';
import {
  FiAward, FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw,
  FiUsers, FiX, FiCheck, FiCheckCircle, FiZap, FiTrash
} from 'react-icons/fi';
import ContestService from '../services/ContestService';
import CategoryService from '../services/CategoryService';
import WinnerService from '../services/WinnerService';
import api from '../services/AxiosService';

const statusColors = {
  LIVE: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  PENDING_APPROVAL: 'bg-amber-50 text-amber-800 border-amber-200',
  PENDING: 'bg-amber-50 text-amber-800 border-amber-200',
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  ENDED: 'bg-rose-50 text-rose-800 border-rose-200',
  EXPIRED: 'bg-rose-50 text-rose-800 border-rose-200',
  COMPLETED: 'bg-purple-50 text-purple-800 border-purple-200',
};

const Contests = () => {
  const [contests, setContests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | { edit: item } | { submissions: item }
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  // Declare Winner State
  const [winnerModal, setWinnerModal] = useState(null); // contest object or null
  const [candidates, setCandidates] = useState([]);
  const [declaredWinners, setDeclaredWinners] = useState([]);
  const [winnerLoading, setWinnerLoading] = useState(false);
  const [winnerSubmitting, setWinnerSubmitting] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [winnerForm, setWinnerForm] = useState({
    rank: 1,
    prize: '',
    certificateUrl: '',
    markCompleted: true,
  });

  const [form, setForm] = useState({
    title: '',
    format: 'PHOTO',
    category: '',
    startDate: '',
    endDate: '',
    entryFee: 0,
    prizePool: 5000,
    rules: '',
    description: '',
    status: 'LIVE',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [resAll, resPartner, resCats] = await Promise.all([
        ContestService.getAllContests().catch(() => []),
        api.get('/contests/partner/all').catch(() => []),
        CategoryService.getAll().catch(() => []),
      ]);

      const listAll = Array.isArray(resAll) ? resAll : (resAll.contests || resAll.data || []);
      const listPartner = Array.isArray(resPartner) ? resPartner : (resPartner.contests || resPartner.data || []);

      const map = new Map();
      [...listAll, ...listPartner].forEach(item => {
        if (item && item._id) map.set(item._id.toString(), item);
      });

      setContests(Array.from(map.values()));
      const catList = Array.isArray(resCats) ? resCats : (resCats.categories || resCats.data || []);
      setCategories(catList);
    } catch (err) {
      console.error('Failed to load contests:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = contests.filter((c) => {
    const title = (c.title || c.name || '').toLowerCase();
    const matchesSearch = title.includes(search.toLowerCase());

    const endDateVal = c.endDate || c.expiryDate || c.schedule?.endAt;
    const rawStatus = (c.status || '').toUpperCase();
    const isPending = rawStatus === 'PENDING_APPROVAL' || rawStatus === 'PENDING';
    const isCompleted = rawStatus === 'COMPLETED';
    const isExpired = !isPending && !isCompleted && endDateVal ? new Date(endDateVal) < new Date() : false;
    const status = isPending ? 'PENDING_APPROVAL' : (isCompleted ? 'COMPLETED' : (isExpired ? 'EXPIRED' : (c.status || (c.isLive !== false ? 'LIVE' : 'DRAFT')).toUpperCase()));

    const matchesStatus = !statusFilter || status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openAdd = () => {
    setForm({
      title: '',
      format: 'PHOTO',
      category: categories[0]?.name || 'Generative Art',
      startDate: new Date().toISOString().slice(0, 16),
      endDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
      entryFee: 0,
      prizePool: 5000,
      rules: 'Submit your original entry. Voting will determine the top winner.',
      description: '',
      status: 'LIVE',
    });
    setModal('add');
  };

  const openEdit = (item) => {
    const endDateVal = item.endDate || item.expiryDate;
    const isExpired = endDateVal ? new Date(endDateVal) < new Date() : false;
    const st = isExpired ? 'EXPIRED' : (item.status || (item.isLive !== false ? 'LIVE' : 'DRAFT')).toUpperCase();

    setForm({
      title: item.title || item.name || '',
      format: item.format || item.contestFormat || item.eventType || 'PHOTO',
      category: typeof item.category === 'object' ? (item.category?.name || '') : (item.category || item.eventCategoryName || categories[0]?.name || ''),
      startDate: item.startDate ? new Date(item.startDate).toISOString().slice(0, 16) : '',
      endDate: endDateVal ? new Date(endDateVal).toISOString().slice(0, 16) : '',
      entryFee: item.entryFee || 0,
      prizePool: item.prizePool || item.cashPrize || 5000,
      rules: item.rules || '',
      description: item.description || '',
      status: st,
    });
    setModal({ edit: item });
  };

  const openSubmissions = async (item) => {
    setModal({ submissions: item });
    setSubsLoading(true);
    try {
      const slugOrId = item.slug || item.contestId || item._id;
      const res = await ContestService.getContestBySlug(slugOrId);
      const list = res.posts || res.leaderboard || (Array.isArray(res) ? res : (res.data || []));
      setSubmissions(list);
    } catch (err) {
      console.error('Failed to load submissions:', err);
      setSubmissions([]);
    } finally {
      setSubsLoading(false);
    }
  };

  // Open Declare Winner modal
  const openDeclareWinner = async (item) => {
    setWinnerModal(item);
    setSelectedCandidate(null);
    const defaultPrize = item.prizeonewords || item.prize || (item.cashPrize ? `₹${item.cashPrize}` : '') || (item.prizePool ? `₹${item.prizePool}` : '₹5,000 Cash Prize');
    setWinnerForm({
      rank: 1,
      prize: defaultPrize,
      certificateUrl: '',
      markCompleted: true,
    });
    setWinnerLoading(true);

    try {
      const contestId = item._id || item.contestId || item.slug;
      const res = await WinnerService.getCandidates(contestId);
      if (res && res.success) {
        setCandidates(res.candidates || []);
        setDeclaredWinners(res.declaredWinners || []);
      } else {
        // Fallback
        const slugOrId = item.slug || item.contestId || item._id;
        const subRes = await ContestService.getContestBySlug(slugOrId);
        const list = subRes.leaderboard || subRes.posts || [];
        setCandidates(list.map((c, i) => ({
          ...c,
          currentLeaderboardRank: i + 1,
          totalVotes: c.voteCount || c.totalVotes || 0,
        })));
        const winRes = await WinnerService.getWinnersByContest(contestId).catch(() => ({ winners: [] }));
        setDeclaredWinners(winRes.winners || []);
      }
    } catch (err) {
      console.error('Failed to load candidates for winner declaration:', err);
      try {
        const slugOrId = item.slug || item.contestId || item._id;
        const subRes = await ContestService.getContestBySlug(slugOrId);
        const list = subRes.leaderboard || subRes.posts || [];
        setCandidates(list.map((c, i) => ({
          ...c,
          currentLeaderboardRank: i + 1,
          totalVotes: c.voteCount || c.totalVotes || 0,
        })));
      } catch (_) {
        setCandidates([]);
      }
      setDeclaredWinners([]);
    } finally {
      setWinnerLoading(false);
    }
  };

  const handleSelectCandidateToAward = (cand, targetRank = 1) => {
    setSelectedCandidate(cand);
    const defaultPrize = winnerModal?.prizeonewords || winnerModal?.prize || (winnerModal?.cashPrize ? `₹${winnerModal?.cashPrize}` : '') || (winnerModal?.prizePool ? `₹${winnerModal?.prizePool}` : '₹5,000 Cash Prize');
    setWinnerForm({
      rank: targetRank,
      prize: cand.winnerPrize || defaultPrize,
      certificateUrl: cand.certificateUrl || '',
      markCompleted: true,
    });
  };

  const handleDeclareWinner = async () => {
    if (!selectedCandidate) {
      alert('Please select a participant entry to declare as winner.');
      return;
    }
    setWinnerSubmitting(true);
    try {
      const payload = {
        contestId: winnerModal._id || winnerModal.contestId,
        userId: selectedCandidate.userId?._id || selectedCandidate.userId,
        rank: Number(winnerForm.rank || 1),
        prize: winnerForm.prize,
        photoUrl: selectedCandidate.postImageUrl || selectedCandidate.image || '',
        totalVotes: Number(selectedCandidate.totalVotes || selectedCandidate.voteCount || 0),
        certificateUrl: winnerForm.certificateUrl,
        markCompleted: winnerForm.markCompleted,
      };

      await WinnerService.declareWinner(payload);
      alert(`🎉 Winner declared successfully for Rank #${winnerForm.rank}! It is now visible on the Winners page in App & Web.`);
      setSelectedCandidate(null);
      openDeclareWinner(winnerModal);
      loadData();
    } catch (err) {
      alert('Failed to declare winner: ' + (err.response?.data?.message || err.message));
    } finally {
      setWinnerSubmitting(false);
    }
  };

  const handleAutoDeclareTop3 = async () => {
    if (!candidates || candidates.length === 0) {
      alert('No candidates found in this contest to declare as winners.');
      return;
    }
    const topCount = Math.min(3, candidates.length);
    if (!window.confirm(`Are you sure you want to automatically declare the top ${topCount} leaderboard participant(s) as winners?`)) return;

    setWinnerSubmitting(true);
    try {
      const prizePool = Number(winnerModal.prizePool || winnerModal.cashPrize || 5000);
      const prizeSplits = topCount === 1
        ? [`₹${prizePool} Cash Prize`]
        : topCount === 2
          ? [`₹${Math.round(prizePool * 0.6)} Cash Prize (1st)`, `₹${Math.round(prizePool * 0.4)} Cash Prize (2nd)`]
          : [`₹${Math.round(prizePool * 0.5)} Cash Prize (1st)`, `₹${Math.round(prizePool * 0.3)} Cash Prize (2nd)`, `₹${Math.round(prizePool * 0.2)} Cash Prize (3rd)`];

      const winnersPayload = candidates.slice(0, topCount).map((c, idx) => ({
        userId: c.userId?._id || c.userId,
        rank: idx + 1,
        prize: prizeSplits[idx] || `₹${Math.round(prizePool / topCount)} Cash Prize`,
        photoUrl: c.postImageUrl || c.image || '',
        totalVotes: Number(c.totalVotes || c.voteCount || 0),
        certificateUrl: '',
      }));

      await WinnerService.declareWinnersBulk({
        contestId: winnerModal._id || winnerModal.contestId,
        winners: winnersPayload,
        markCompleted: true,
      });

      alert(`🎉 Successfully declared top ${topCount} winner(s)! They are now live on the Winners page in App & Web.`);
      openDeclareWinner(winnerModal);
      loadData();
    } catch (err) {
      alert('Failed to auto-declare winners: ' + (err.response?.data?.message || err.message));
    } finally {
      setWinnerSubmitting(false);
    }
  };

  const handleDeleteDeclaredWinner = async (winnerId) => {
    if (!window.confirm('Are you sure you want to remove this declared winner?')) return;
    try {
      await WinnerService.deleteWinner(winnerId);
      openDeclareWinner(winnerModal);
      loadData();
    } catch (err) {
      alert('Failed to remove winner: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: form.title,
        name: form.title,
        type: form.format,
        format: form.format,
        contestFormat: form.format,
        category: form.category,
        startDate: form.startDate,
        endDate: form.endDate,
        expiryDate: form.endDate,
        entryFee: Number(form.entryFee || 0),
        prizePool: Number(form.prizePool || 5000),
        cashPrize: Number(form.prizePool || 5000),
        prizeonewords: `₹${form.prizePool || 5000} Cash Prize`,
        prize: `₹${form.prizePool || 5000} Cash Prize`,
        rules: form.rules,
        description: form.description,
        status: form.status,
        isLive: form.status === 'LIVE',
      };

      if (modal === 'add') {
        await ContestService.createContest(payload);
      } else if (modal?.edit) {
        await ContestService.updateContest(modal.edit._id, payload);
      }
      setModal(null);
      loadData();
    } catch (err) {
      alert('Failed to save contest: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contest?')) return;
    try {
      await ContestService.deleteContest(id);
      loadData();
    } catch (err) {
      alert('Failed to delete contest: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleApproveContest = async (item) => {
    try {
      await ContestService.updateContest(item._id, { status: 'LIVE', isLive: true });
      loadData();
    } catch (err) {
      alert('Failed to approve contest: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="space-y-6 max-w-7xl text-left">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2 tracking-tight">
            <FiAward className="text-purple-700" /> Contests & Challenges
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Manage photo & video challenges and crown winners on App & Web</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh">
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="px-4 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm">
            <FiPlus className="w-4 h-4" /> Add Contest
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contests by title..."
            className="w-full bg-white border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 transition shadow-sm"
          />
        </div>
        <div className="flex gap-1.5 bg-white border border-slate-300 rounded-xl p-1 shadow-sm overflow-x-auto">
          {['', 'PENDING_APPROVAL', 'LIVE', 'COMPLETED', 'DRAFT', 'EXPIRED'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition whitespace-nowrap ${statusFilter === s ? 'bg-purple-700 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              {s === 'PENDING_APPROVAL' ? 'Pending' : (s === 'COMPLETED' ? 'Completed / Winner' : (s || 'All'))}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FiAward className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-500">No contests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/70">
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Contest</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Format</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Prize / Fee</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">End Date</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const catName = typeof c.category === 'object' ? c.category?.name : (c.category || c.eventCategoryName || 'General');
                  const format = c.format || c.contestFormat || c.eventType || (c.type !== 'Private' && c.type) || 'PHOTO';
                  const prize = c.prizeonewords || c.prize || (c.cashPrize ? `₹${c.cashPrize}` : '') || (c.prizePool ? `₹${c.prizePool}` : '₹5000');
                  const fee = c.entryFee || 0;

                  const endDateVal = c.endDate || c.expiryDate || c.schedule?.endAt;
                  const rawStatus = (c.status || '').toUpperCase();
                  const isPending = rawStatus === 'PENDING_APPROVAL' || rawStatus === 'PENDING';
                  const isCompleted = rawStatus === 'COMPLETED';
                  const isExpired = !isPending && !isCompleted && endDateVal ? new Date(endDateVal) < new Date() : false;
                  const status = isPending ? 'PENDING_APPROVAL' : (isCompleted ? 'COMPLETED' : (isExpired ? 'EXPIRED' : (c.status || (c.isLive !== false ? 'LIVE' : 'DRAFT')).toUpperCase()));

                  return (
                    <tr key={c._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-xs font-bold text-slate-900 truncate max-w-[240px]">{c.title || c.name}</p>
                            <p className="text-[10px] text-slate-500 font-medium truncate">{c.description || 'Contest Challenge'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-xs font-semibold text-slate-700">{catName}</td>
                      <td className="px-4 py-3.5 text-xs text-purple-700 font-bold">{format}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-900">
                        <span className="text-emerald-700 font-bold">{prize}</span>
                        <span className="text-[10px] text-slate-500 font-medium ml-1.5">({fee === 0 ? 'FREE' : `Fee ₹${fee}`})</span>
                      </td>
                      <td className="px-4 py-3.5 text-xs font-medium text-slate-600">
                        {endDateVal ? new Date(endDateVal).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-extrabold border ${statusColors[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Declare Winner Button */}
                          <button
                            onClick={() => openDeclareWinner(c)}
                            className="px-2.5 py-1 rounded-lg text-[10px] font-extrabold bg-amber-500 hover:bg-amber-600 text-white transition flex items-center gap-1 shadow-xs"
                            title="Declare / Manage Winners for this Contest"
                          >
                            <FiAward className="w-3 h-3" /> Winner
                          </button>

                          {(status === 'PENDING_APPROVAL' || status === 'PENDING') && (
                            <button
                              onClick={() => handleApproveContest(c)}
                              className="px-2.5 py-1 rounded-lg text-[10px] font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 transition flex items-center gap-1 shadow-xs"
                              title="Approve & Make Live"
                            >
                              <FiCheck className="w-3 h-3" /> Approve
                            </button>
                          )}
                          <button onClick={() => openSubmissions(c)} className="p-1.5 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition" title="View Submissions">
                            <FiUsers className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition" title="Edit">
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(c._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition" title="Delete">
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal?.edit) && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold text-slate-900">{modal === 'add' ? 'Create Contest' : 'Edit Contest'}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Contest Title *</label>
                <input
                  type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Winter Fashion Photography 2026"
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Description / Tagline</label>
                <input
                  type="text" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Short description of the contest..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Format</label>
                  <select value={form.format} onChange={e => setForm(p => ({ ...p, format: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                    <option value="PHOTO">PHOTO</option>
                    <option value="VIDEO">VIDEO</option>
                    <option value="REEL">REEL</option>
                    <option value="ART">ART</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                    {categories.map((cat, idx) => (
                      <option key={cat._id || idx} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                    <option value="LIVE">LIVE</option>
                    <option value="PENDING_APPROVAL">PENDING_APPROVAL</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="DRAFT">DRAFT</option>
                    <option value="ENDED">ENDED</option>
                    <option value="EXPIRED">EXPIRED</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Prize Pool (₹)</label>
                  <input type="number" value={form.prizePool} onChange={e => setForm(p => ({ ...p, prizePool: Number(e.target.value) }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Entry Fee (₹)</label>
                  <input type="number" value={form.entryFee} onChange={e => setForm(p => ({ ...p, entryFee: Number(e.target.value) }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Start Date</label>
                  <input type="datetime-local" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">End Date</label>
                  <input type="datetime-local" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Rules / Criteria</label>
                <textarea value={form.rules} onChange={e => setForm(p => ({ ...p, rules: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 h-20 resize-none font-medium" />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition">Save Contest</button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {modal?.submissions && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-3xl space-y-4 max-h-[85vh] overflow-y-auto text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FiUsers className="text-purple-700" /> Submissions for "{modal.submissions.title || modal.submissions.name}"
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">{submissions.length} total entries</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const item = modal.submissions;
                    setModal(null);
                    openDeclareWinner(item);
                  }}
                  className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition flex items-center gap-1 shadow-xs"
                >
                  <FiAward className="w-3.5 h-3.5" /> Declare Winners
                </button>
                <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600 p-1">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            </div>

            {subsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-500 font-medium text-xs">No entries submitted for this contest yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {submissions.map((sub, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden p-2 text-left relative group">
                    {sub.postImageUrl || sub.postUrl || sub.mediaUrl || sub.image || sub.postImage?.url ? (
                      <img src={sub.postImageUrl || sub.postUrl || sub.mediaUrl || sub.image || sub.postImage?.url} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />
                    ) : (
                      <div className="w-full h-32 bg-slate-200 rounded-lg mb-2 flex items-center justify-center text-slate-500 text-xs font-medium">No Media</div>
                    )}
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-900 truncate">{sub.name || sub.userName || sub.userDetails?.name || sub.userId?.name || 'Participant'}</p>
                      <span className="text-[10px] text-purple-700 font-extrabold">{sub.voteCount || sub.totalVotes || sub.votesCount || 0} Votes</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* DECLARE WINNERS MODAL */}
      {winnerModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setWinnerModal(null)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 w-full max-w-4xl space-y-6 max-h-[92vh] overflow-y-auto text-left shadow-2xl" onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-200 gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded-lg bg-amber-100 text-amber-700">
                    <FiAward className="w-5 h-5" />
                  </span>
                  <h2 className="text-lg font-black text-slate-900">Declare Contest Winners</h2>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase ${statusColors[winnerModal.status] || 'bg-slate-100 text-slate-700'}`}>
                    {winnerModal.status || 'LIVE'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Contest: <strong className="text-slate-800">{winnerModal.title || winnerModal.name}</strong> • Prize Pool: <strong className="text-emerald-700">{winnerModal.prizeonewords || winnerModal.prize || `₹${winnerModal.prizePool || 5000}`}</strong>
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleAutoDeclareTop3}
                  disabled={winnerSubmitting || candidates.length === 0}
                  className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold shadow-sm transition flex items-center gap-1.5 disabled:opacity-50"
                  title="Automatically award 1st, 2nd, and 3rd rank to top vote earners"
                >
                  <FiZap className="w-4 h-4 fill-white" /> Auto-Declare Top 3
                </button>
                <button onClick={() => setWinnerModal(null)} className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition">
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {winnerLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-8 h-8 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                <p className="text-xs text-slate-500 font-semibold">Loading contest submissions & current winners...</p>
              </div>
            ) : (
              <>
                {/* Section 1: CURRENT DECLARED WINNERS */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                      <FiAward className="text-amber-500" /> Declared Winners (Live on App & Web)
                    </h3>
                    <span className="text-[11px] font-bold text-slate-500">{declaredWinners.length} winner(s)</span>
                  </div>

                  {declaredWinners.length === 0 ? (
                    <div className="bg-white border border-dashed border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 font-medium">
                        No winners announced yet. Select a candidate from the leaderboard below to declare them as winner!
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {declaredWinners.map((win) => {
                        const rankNum = win.rank || 1;
                        const rankBadgeColor = rankNum === 1
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : rankNum === 2
                            ? 'bg-slate-200 text-slate-800 border-slate-300'
                            : rankNum === 3
                              ? 'bg-orange-100 text-orange-800 border-orange-300'
                              : 'bg-purple-100 text-purple-800 border-purple-200';

                        const rankIcon = rankNum === 1 ? '🥇' : rankNum === 2 ? '🥈' : rankNum === 3 ? '🥉' : '🏆';
                        const uName = win.userId?.name || win.userDetails?.name || 'Winner';
                        const uPic = win.userId?.profilePic || win.userDetails?.profilePic || win.userDetails?.profileUrl || '';

                        return (
                          <div key={win._id} className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs relative flex flex-col justify-between">
                            <div className="flex items-start gap-3">
                              <div className="relative">
                                {win.photoUrl ? (
                                  <img src={win.photoUrl} alt="" className="w-14 h-14 object-cover rounded-xl border border-slate-200" />
                                ) : (
                                  <div className="w-14 h-14 bg-slate-200 rounded-xl flex items-center justify-center text-xs font-bold text-slate-500">No Img</div>
                                )}
                                <span className="absolute -top-2 -left-2 text-base leading-none drop-shadow-xs">{rankIcon}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${rankBadgeColor}`}>
                                    Rank #{rankNum}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  {uPic && (
                                    <img src={uPic} alt="" className="w-4 h-4 rounded-full object-cover" />
                                  )}
                                  <p className="text-xs font-extrabold text-slate-900 truncate">{uName}</p>
                                </div>
                                <p className="text-[10px] font-bold text-emerald-700 truncate">{win.prize || 'Award Winner'}</p>
                                <p className="text-[10px] text-purple-700 font-bold mt-0.5">{win.totalVotes || 0} Votes</p>
                              </div>
                            </div>

                            <div className="mt-3 pt-2 border-t border-slate-100 flex justify-between items-center">
                              <span className="text-[9px] text-slate-400 font-semibold">
                                {win.createdAt ? new Date(win.createdAt).toLocaleDateString() : ''}
                              </span>
                              <button
                                onClick={() => handleDeleteDeclaredWinner(win._id)}
                                className="text-[10px] font-bold text-rose-600 hover:text-rose-800 hover:bg-rose-50 px-2 py-1 rounded-md transition flex items-center gap-1"
                                title="Remove winner status"
                              >
                                <FiTrash className="w-3 h-3" /> Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Section 2: CANDIDATE SELECTION FORM (If candidate clicked) */}
                {selectedCandidate && (
                  <div className="bg-purple-50 border-2 border-purple-300 rounded-2xl p-5 space-y-4 shadow-sm animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-extrabold text-purple-900 uppercase tracking-wider flex items-center gap-1.5">
                        <FiAward className="text-purple-700" /> Award Winner: {selectedCandidate.name}
                      </h4>
                      <button onClick={() => setSelectedCandidate(null)} className="text-slate-400 hover:text-slate-600">
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white p-3 rounded-xl border border-purple-200">
                      {selectedCandidate.postImageUrl ? (
                        <img src={selectedCandidate.postImageUrl} alt="" className="w-16 h-16 object-cover rounded-xl border border-slate-200" />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-xs font-bold text-slate-500">No Image</div>
                      )}
                      <div className="flex-1">
                        <p className="text-xs font-extrabold text-slate-900">{selectedCandidate.name} (@{selectedCandidate.username || 'user'})</p>
                        <p className="text-[11px] text-slate-600 font-medium">Leaderboard Position: <strong>#{selectedCandidate.currentLeaderboardRank}</strong> • Total Votes: <strong className="text-purple-700">{selectedCandidate.totalVotes || 0}</strong></p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Rank / Position *</label>
                        <select
                          value={winnerForm.rank}
                          onChange={(e) => setWinnerForm(p => ({ ...p, rank: Number(e.target.value) }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                        >
                          <option value={1}>Rank 1 (🥇 1st Place)</option>
                          <option value={2}>Rank 2 (🥈 2nd Place)</option>
                          <option value={3}>Rank 3 (🥉 3rd Place)</option>
                          <option value={4}>Rank 4 (4th Place)</option>
                          <option value={5}>Rank 5 (5th Place)</option>
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Prize Description *</label>
                        <input
                          type="text"
                          value={winnerForm.prize}
                          onChange={(e) => setWinnerForm(p => ({ ...p, prize: e.target.value }))}
                          placeholder="e.g. ₹5,000 Cash Prize + Certificate"
                          className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Certificate Image URL (Optional)</label>
                      <input
                        type="url"
                        value={winnerForm.certificateUrl}
                        onChange={(e) => setWinnerForm(p => ({ ...p, certificateUrl: e.target.value }))}
                        placeholder="https://... certificate image link"
                        className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-purple-200">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                        <input
                          type="checkbox"
                          checked={winnerForm.markCompleted}
                          onChange={(e) => setWinnerForm(p => ({ ...p, markCompleted: e.target.checked }))}
                          className="w-4 h-4 text-purple-700 rounded-sm focus:ring-purple-600"
                        />
                        Mark Contest Status as COMPLETED
                      </label>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedCandidate(null)}
                          className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-300 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeclareWinner}
                          disabled={winnerSubmitting}
                          className="px-5 py-2 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold shadow-md transition flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {winnerSubmitting ? 'Declaring...' : 'Confirm & Declare Winner'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 3: CANDIDATES LEADERBOARD */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider">
                        Contest Participants & Leaderboard
                      </h3>
                      <p className="text-[11px] text-slate-500 font-medium">Sorted by total votes received. Click any participant to declare as winner.</p>
                    </div>
                    <span className="text-xs font-extrabold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg">
                      {candidates.length} Participants
                    </span>
                  </div>

                  {candidates.length === 0 ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl py-12 text-center">
                      <FiUsers className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs font-bold text-slate-600">No participants found in this contest yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {candidates.map((cand) => {
                        const isWin = cand.isDeclaredWinner;
                        const rankMedal = cand.currentLeaderboardRank === 1 ? '🥇' : cand.currentLeaderboardRank === 2 ? '🥈' : cand.currentLeaderboardRank === 3 ? '🥉' : `#${cand.currentLeaderboardRank}`;

                        return (
                          <div
                            key={cand.postId || cand.userId}
                            className={`border rounded-2xl p-3 flex flex-col justify-between transition-all ${
                              isWin
                                ? 'bg-amber-50/50 border-amber-300 shadow-xs'
                                : 'bg-white border-slate-200 hover:border-purple-400 hover:shadow-sm'
                            }`}
                          >
                            <div>
                              <div className="relative rounded-xl overflow-hidden mb-2.5 bg-slate-100">
                                {cand.postImageUrl ? (
                                  <img src={cand.postImageUrl} alt="" className="w-full h-36 object-cover" />
                                ) : (
                                  <div className="w-full h-36 flex items-center justify-center text-xs text-slate-400 font-medium">No Media</div>
                                )}
                                <span className="absolute top-2 left-2 bg-slate-900/80 backdrop-blur-xs text-white text-[11px] font-black px-2 py-0.5 rounded-lg">
                                  {rankMedal}
                                </span>
                                <span className="absolute top-2 right-2 bg-purple-700/90 backdrop-blur-xs text-white text-[10px] font-extrabold px-2 py-0.5 rounded-lg">
                                  {cand.totalVotes || cand.voteCount || 0} Votes
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                                  {cand.profilePic ? (
                                    <img src={cand.profilePic} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    (cand.name || 'U').charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-extrabold text-slate-900 truncate">{cand.name}</p>
                                  <p className="text-[10px] text-slate-500 font-medium truncate">@{cand.username || 'user'}</p>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 pt-2.5 border-t border-slate-100">
                              {isWin ? (
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-extrabold text-amber-700 flex items-center gap-1">
                                    <FiCheckCircle className="w-3.5 h-3.5 text-amber-600" /> Winner (Rank #{cand.winnerRank})
                                  </span>
                                  <button
                                    onClick={() => handleSelectCandidateToAward(cand, cand.winnerRank || 1)}
                                    className="text-[10px] font-bold text-slate-600 hover:text-purple-700 underline"
                                  >
                                    Edit
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSelectCandidateToAward(cand, cand.currentLeaderboardRank <= 3 ? cand.currentLeaderboardRank : 1)}
                                  className="w-full py-2 rounded-xl bg-slate-100 hover:bg-purple-700 hover:text-white text-slate-700 text-xs font-extrabold transition flex items-center justify-center gap-1.5"
                                >
                                  <FiAward className="w-3.5 h-3.5" /> Declare as Winner
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end pt-3 border-t border-slate-200">
              <button
                onClick={() => setWinnerModal(null)}
                className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contests;
